import React, { useRef, useEffect } from 'react';
import { IncompleteProduct } from '@/services/dashboardService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDashboardTranslations } from '@/hooks/useDashboardTranslations';

interface IncompleteProductsListProps {
  products: IncompleteProduct[] | null;
  loading: boolean;
  maxItems?: number;
  title?: string;
  description?: string;
  showViewAll?: boolean;
}

export const IncompleteProductsList: React.FC<IncompleteProductsListProps> = ({
  products,
  loading,
  maxItems = 5,
  title,
  description,
  showViewAll = false,
}) => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const i18n = useDashboardTranslations();

  // Use translations if title/description not provided
  const displayTitle = title || i18n.getIncompleteProductsText('title');
  const displayDescription = description || i18n.getIncompleteProductsText('description');

  // Track component mount state to prevent navigation after unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleRowClick = (productId?: number) => {
    // Prevent navigation if component is unmounted or productId is invalid
    if (!isMountedRef.current || !productId) {
      return;
    }

    // Use setTimeout to ensure DOM operations complete before navigation
    setTimeout(() => {
      if (isMountedRef.current) {
        navigate(`/app/products/${productId}`);
      }
    }, 0);
  };

  // Helper function to translate field names
  const translateFieldName = (fieldName: string): string => {
    // Convert to lowercase and remove spaces for lookup
    const normalizedField = fieldName.toLowerCase().replace(/\s+/g, '');

    // Try to get translation, fallback to original if not found
    const translatedField = i18n.getIncompleteProductsText(`table.fieldNames.${normalizedField}`);

    // If translation is the same as the key (meaning not found), return original
    if (
      translatedField ===
      `dashboardpage.cards.incompleteProducts.table.fieldNames.${normalizedField}`
    ) {
      return fieldName;
    }

    return translatedField;
  };

  return (
    <Card className="border-enterprise-200 bg-white shadow-sm dark:border-border dark:bg-card">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-200">
            {displayTitle}
          </CardTitle>
          <CardDescription className="text-enterprise-500 dark:text-muted-foreground">
            {displayDescription}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-3 p-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-5 w-4/5" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
        ) : products && products.length > 0 ? (
          <div className="divide-y divide-enterprise-100 dark:divide-border">
            {products.slice(0, maxItems).map(product => (
              <div
                key={product?.id || Math.random()}
                className="cursor-pointer px-6 py-4 hover:bg-enterprise-50 dark:hover:bg-muted"
                onClick={() => handleRowClick(product?.id)}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-medium text-enterprise-800 dark:text-foreground">
                    {product?.name || i18n.getIncompleteProductsText('unnamedProduct')}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-enterprise-50 dark:border-border dark:bg-muted dark:text-foreground"
                  >
                    {i18n.getIncompleteProductsText('percentComplete', {
                      percent: product?.completeness || 0,
                    })}
                  </Badge>
                </div>
                <div className="mb-3 text-xs text-enterprise-500 dark:text-muted-foreground">
                  {i18n.getIncompleteProductsText('skuLabel', {
                    sku: product?.sku || i18n.getIncompleteProductsText('noSku'),
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {product?.missing_fields && Array.isArray(product.missing_fields) ? (
                    product.missing_fields.slice(0, 5).map(field => {
                      const translatedField = translateFieldName(field);
                      const displayField =
                        translatedField.length > 24
                          ? `${translatedField.slice(0, 21)}...`
                          : translatedField;

                      return (
                        <Badge
                          key={field}
                          variant="outline"
                          className="max-w-xs truncate border-danger-200 bg-danger-50 text-danger-700 dark:border-red-800 dark:bg-red-950/20 dark:text-red-400"
                          title={translatedField}
                        >
                          {i18n.getIncompleteProductsText('missingField', {
                            field: displayField,
                          })}
                        </Badge>
                      );
                    })
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-danger-200 bg-danger-50 text-danger-700 dark:border-red-800 dark:bg-red-950/20 dark:text-red-400"
                    >
                      {i18n.getIncompleteProductsText('missingData')}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle2 className="mb-2 size-8 text-success-500 dark:text-green-400" />
            <p className="font-medium text-enterprise-700 dark:text-foreground">
              {i18n.getIncompleteProductsText('allCompleteTitle')}
            </p>
            <p className="text-sm text-enterprise-500 dark:text-muted-foreground">
              {i18n.getIncompleteProductsText('allCompleteDesc')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
